import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';
import {
  normalizeSeatPosition,
  type ClassMemberSeatPosition,
} from './classMembersStorage';

export const CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID = 'default';
export const CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME = '기본';

export type ClassMemberSeatLayoutPreset = {
  id: string;
  name: string;
  isDefault: boolean;
  /** memberId → 좌표 */
  seats: Record<string, ClassMemberSeatPosition>;
  updatedAt: number;
};

export type ClassMemberSeatLayoutPresetsState = {
  v: 1;
  appliedPresetId: string;
  presets: ClassMemberSeatLayoutPreset[];
};

const STORAGE_PREFIX = 'cp:class-seat-layout-presets:v1:';

function storageKey(workspaceId: string, gradeId: string, groupId: string): string {
  return `${STORAGE_PREFIX}${workspaceId.trim()}:${gradeId.trim()}:${groupId.trim()}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function seatsMapToRecord(
  seats: Map<string, ClassMemberSeatPosition> | ReadonlyMap<string, ClassMemberSeatPosition>
): Record<string, ClassMemberSeatPosition> {
  const out: Record<string, ClassMemberSeatPosition> = {};
  seats.forEach((pos, id) => {
    const key = id.trim();
    if (!key) return;
    out[key] = { row: pos.row, col: pos.col };
  });
  return out;
}

export function seatsRecordToMap(
  seats: Record<string, ClassMemberSeatPosition> | null | undefined
): Map<string, ClassMemberSeatPosition> {
  const out = new Map<string, ClassMemberSeatPosition>();
  if (!seats || typeof seats !== 'object') return out;
  Object.entries(seats).forEach(([id, raw]) => {
    const key = id.trim();
    const pos = normalizeSeatPosition(raw);
    if (!key || !pos) return;
    out.set(key, pos);
  });
  return out;
}

function normalizePreset(raw: unknown, fallbackDefault = false): ClassMemberSeatLayoutPreset | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  const isDefault = o.isDefault === true || id === CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID;
  const nameRaw = String(o.name ?? '').trim();
  const name =
    nameRaw ||
    (isDefault ? CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME : '자리배치');
  const seats: Record<string, ClassMemberSeatPosition> = {};
  if (o.seats && typeof o.seats === 'object') {
    Object.entries(o.seats as Record<string, unknown>).forEach(([memberId, posRaw]) => {
      const mid = memberId.trim();
      const pos = normalizeSeatPosition(posRaw);
      if (!mid || !pos) return;
      seats[mid] = pos;
    });
  }
  const updatedAt = Number(o.updatedAt);
  return {
    id: isDefault ? CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID : id,
    name: isDefault ? name || CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME : name,
    isDefault: isDefault || fallbackDefault,
    seats,
    updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : Date.now(),
  };
}

export function createDefaultSeatLayoutPreset(
  seats: Record<string, ClassMemberSeatPosition> = {}
): ClassMemberSeatLayoutPreset {
  return {
    id: CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID,
    name: CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME,
    isDefault: true,
    seats: { ...seats },
    updatedAt: Date.now(),
  };
}

export function ensureSeatLayoutPresetsState(
  state: ClassMemberSeatLayoutPresetsState | null | undefined,
  seedSeats?: Record<string, ClassMemberSeatPosition>
): ClassMemberSeatLayoutPresetsState {
  const presets = Array.isArray(state?.presets)
    ? state!.presets.map((p) => normalizePreset(p)).filter((p): p is ClassMemberSeatLayoutPreset => !!p)
    : [];
  let defaultPreset = presets.find((p) => p.isDefault || p.id === CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID);
  if (!defaultPreset) {
    defaultPreset = createDefaultSeatLayoutPreset(seedSeats ?? {});
    presets.unshift(defaultPreset);
  } else if (
    seedSeats &&
    Object.keys(defaultPreset.seats).length === 0 &&
    Object.keys(seedSeats).length > 0
  ) {
    defaultPreset = {
      ...defaultPreset,
      seats: { ...seedSeats },
      updatedAt: Date.now(),
    };
  }
  const others = presets.filter((p) => p.id !== CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID);
  const nextPresets = [
    {
      ...defaultPreset,
      id: CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID,
      isDefault: true,
      name: defaultPreset.name.trim() || CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME,
    },
    ...others.map((p) => ({ ...p, isDefault: false })),
  ];
  const appliedRaw = String(state?.appliedPresetId ?? '').trim();
  const appliedPresetId = nextPresets.some((p) => p.id === appliedRaw)
    ? appliedRaw
    : CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID;
  return { v: 1, appliedPresetId, presets: nextPresets };
}

export function loadClassMemberSeatLayoutPresets(params: {
  workspaceId: string;
  gradeId: string;
  groupId: string;
  seedSeats?: Record<string, ClassMemberSeatPosition>;
}): ClassMemberSeatLayoutPresetsState {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  const groupId = params.groupId.trim();
  if (!sid || !gid || !groupId) {
    return ensureSeatLayoutPresetsState(null, params.seedSeats);
  }
  const storage = getStorage();
  if (!storage) return ensureSeatLayoutPresetsState(null, params.seedSeats);
  try {
    const raw = storage.getItem(storageKey(sid, gid, groupId));
    if (!raw) return ensureSeatLayoutPresetsState(null, params.seedSeats);
    const parsed = JSON.parse(raw) as ClassMemberSeatLayoutPresetsState;
    return ensureSeatLayoutPresetsState(parsed, params.seedSeats);
  } catch {
    return ensureSeatLayoutPresetsState(null, params.seedSeats);
  }
}

export function saveClassMemberSeatLayoutPresets(params: {
  workspaceId: string;
  gradeId: string;
  groupId: string;
  state: ClassMemberSeatLayoutPresetsState;
}): void {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  const groupId = params.groupId.trim();
  if (!sid || !gid || !groupId) return;
  const normalized = ensureSeatLayoutPresetsState(params.state);
  const storage = getStorage();
  if (!storage) return;
  try {
    localStorageSetItemWithQuotaRecovery(storageKey(sid, gid, groupId), JSON.stringify(normalized));
  } catch {
    /* ignore */
  }
}

export function createSeatLayoutPresetId(): string {
  return `seat-layout-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
